/**
 * Thin fetch wrapper for the JSON API.
 *
 * Same-origin in production (FastAPI serves this bundle), proxied by Vite in dev,
 * so there is no base URL to configure and no CORS to think about.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** Pull a readable sentence out of whichever error shape FastAPI returned.
 *
 * There are two, and they do not look alike. `HTTPException(detail="...")`
 * yields a plain string, but a Pydantic validation failure yields a *list* of
 * objects — `[{loc, msg, type, ...}]`. Both used to go through `String()`,
 * which turns the list into the literal text "[object Object]". Every
 * validation message the API took care to write was thrown away at the last
 * step and shown to the user as a JavaScript stringification artefact.
 */
export function errorMessage(payload: unknown, fallback: string): string {
  if (typeof payload !== "object" || payload === null || !("detail" in payload)) {
    return fallback;
  }

  const detail = (payload as { detail: unknown }).detail;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const sentences = detail
      .map((item) => {
        const msg = (item as { msg?: unknown })?.msg;
        // Pydantic prefixes messages raised by a custom validator. The prefix
        // names the machinery, not the problem, so it goes.
        return typeof msg === "string" ? msg.replace(/^Value error,\s*/, "") : null;
      })
      .filter((m): m is string => Boolean(m));

    if (sentences.length > 0) return [...new Set(sentences)].join(" ");
  }

  return fallback;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers:
      init?.body instanceof FormData
        ? undefined
        : { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(payload, response.statusText), payload);
  }

  return payload as T;
}

/**
 * Which vessel the scoped calls below are about.
 *
 * Module state rather than a parameter on all 57 call sites, and rather than a
 * React context those call sites would each have to consume. The alternative
 * was threading an id through every screen, hook and component that touches the
 * API — a change that would have touched far more code than the feature, and
 * where the failure mode of forgetting one is silently reading the wrong
 * pool's chemistry.
 *
 * `VesselRoute` sets this from the URL before rendering anything beneath it, so
 * the id in the address bar is always the one the requests carry. Nothing else
 * writes it.
 */
let currentVesselId: number | null = null;

export function setCurrentVessel(id: number | null): void {
  currentVesselId = id;
}

export function currentVessel(): number | null {
  return currentVesselId;
}

/**
 * Expand a vessel-scoped path.
 *
 * Callers write `/api/readings/latest` exactly as they always did and this puts
 * the vessel in front. Throwing when none is set is deliberate: the alternative
 * is defaulting to vessel 1, which would answer with the pool's readings on the
 * hot tub's screen and look entirely plausible while doing it.
 *
 * Exported for the one caller that cannot go through `api`: `ReportButton`
 * wants a PDF blob rather than JSON, so it calls `fetch` itself — and calling
 * `fetch` itself is exactly how it ended up posting to unscoped paths that no
 * longer exist. Reaching for the same expansion keeps it honest.
 */
export function scopedPath(path: string): string {
  if (currentVesselId === null) {
    throw new ApiError(0, "No pool selected. Pick one first.");
  }
  return `/api/vessels/${currentVesselId}${path.replace(/^\/api/, "")}`;
}

/** Calls about one body of water. Paths are written unscoped and expanded here. */
export const api = {
  get: <T>(path: string) => request<T>(scopedPath(path)),
  post: <T>(path: string, body?: unknown) =>
    request<T>(scopedPath(path), {
      method: "POST",
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(scopedPath(path), { method: "PUT", body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string) => request<T>(scopedPath(path), { method: "DELETE" }),
};

/**
 * Calls that belong to the household rather than to one vessel.
 *
 * Kept as a separate object rather than a flag on `api`, so that reaching for
 * the wrong one is a name you have to type rather than an argument you can
 * forget. Auth, the site info, health, the chemical registry, setup, the
 * vessel list, the postal-code lookup and the volume estimator all live here.
 * Weather does not: since ADR 0019 it is about the vessel's place.
 */
export const globalApi = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, method: "POST" | "PATCH" = "POST") =>
    request<T>(path, {
      method,
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export interface SessionUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  is_admin: boolean;
}

export interface SessionStatus {
  authenticated: boolean;
  /** Whether this household has a vessel yet — what decides between the
   *  setup screen and the picker on boot. */
  setup_complete: boolean;
  user: SessionUser | null;
}

export const SIGNED_OUT: SessionStatus = { authenticated: false, setup_complete: false, user: null };

export interface SiteProvider {
  name: string;
  label: string;
}

/** What a signed-out page may know. Nothing private. */
export interface SiteInfo {
  name: string;
  contact_email: string;
  providers: SiteProvider[];
  signup_mode: "open" | "allowlist" | "closed";
}

export interface Identity {
  provider: string;
  label: string;
  email: string;
  email_verified: boolean;
  last_used_at: string | null;
}

export const auth = {
  me: () => globalApi.get<SessionStatus>("/api/auth/me"),
  identities: () => globalApi.get<Identity[]>("/api/auth/identities"),
  logout: () => globalApi.post<SessionStatus>("/api/auth/logout"),
  deleteAccount: () => globalApi.delete<void>("/api/auth/account"),
  /**
   * Where a sign-in button points. A plain link, not a fetch: the provider's
   * consent screen is a full-page navigation, and the server sets the state
   * cookie on the way out.
   */
  loginUrl: (provider: string, next?: string): string => {
    const params = new URLSearchParams();
    if (next && next !== "/") params.set("next", next);
    if (provider === "mock") params.set("user", "owner");
    const query = params.toString();
    return `/api/auth/login/${provider}${query ? `?${query}` : ""}`;
  },
};

export const site = {
  info: () => globalApi.get<SiteInfo>("/api/site"),
};
